import React from 'react';
import { Card } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Send, FileCheck, CalendarCheck, CheckCircle2, Archive, XCircle, Loader2
} from 'lucide-react';

// Pipeline simplifié — UI orientée business, mapping prudent vers les
// statuts DB existants. Modifier ici impacte le stepper sur la fiche candidat.
const STAGES = [
  {
    id: 'applied',
    label: 'Postulé',
    short: 'Postulé',
    icon: Send,
    matches: ['new', 'cv_received'],
  },
  {
    id: 'form_filled',
    label: 'Formulaire rempli',
    short: 'Formulaire',
    icon: FileCheck,
    matches: ['analyzed', 'qualification_pending', 'prequalified'],
  },
  {
    id: 'selected',
    label: 'Sélectionné',
    short: 'Sélectionné',
    icon: CheckCircle2,
    matches: ['validated'],
  },
  {
    id: 'interviewed',
    label: 'Entretien',
    short: 'Entretien',
    icon: CalendarCheck,
    matches: ['to_call', 'interviewed', 'test_sent', 'test_passed'],
  },
  {
    id: 'talent_pool',
    label: 'Talent pool',
    short: 'Talent pool',
    icon: Archive,
    matches: ['talent_pool', 'assigned'],
  },
];

// Terminal side-track: rejected
const REJECTED_STATUS = 'rejected';

// Simplified dropdown — only the statuses an admin needs to force manually.
// "Garder" / "Rejeter" buttons handle 99% of transitions; this select is the escape hatch.
const STATUS_OPTIONS = [
  { value: 'new', label: '📩  Nouveau (postulé)' },
  { value: 'analyzed', label: '📋  Formulaire rempli' },
  { value: 'validated', label: '⭐  Sélectionné (pré-call)' },
  { value: 'to_call', label: '📅  Entretien à planifier' },
  { value: 'interviewed', label: '✅  Entretien réalisé' },
  { value: 'talent_pool', label: '💎  Talent Pool (présentable client)' },
  { value: 'assigned', label: '💼  Assigné mission' },
  { value: 'rejected', label: '❌  Rejeté' },
];

const CandidatePipelineStatus = ({ candidate, updating, updateStatus }) => {
  const currentStatus = candidate?.status || 'new';
  const isRejected = currentStatus === REJECTED_STATUS;

  // Determine the current stage index
  const currentStageIdx = STAGES.findIndex(s => s.matches.includes(currentStatus));
  // If the status doesn't match any stage (e.g. some legacy status), we still render but highlight none
  const effectiveIdx = isRejected ? -1 : currentStageIdx;

  return (
    <Card className="p-4 md:p-6 border-tp-border" data-testid="pipeline-status-card">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
        {/* Status selector — placed FIRST on mobile, RIGHT on desktop */}
        <div className="flex-shrink-0 w-full lg:w-80 order-1 lg:order-2">
          <label className="text-xs font-semibold text-tp-text-light uppercase tracking-wide mb-1.5 block">
            État du candidat
          </label>
          <Select value={currentStatus} onValueChange={updateStatus} disabled={updating}>
            <SelectTrigger data-testid="pipeline-status-select" className="h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`status-option-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {updating && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-tp-text-light">
              <Loader2 className="h-3 w-3 animate-spin" />
              Mise à jour en cours…
            </div>
          )}
        </div>

        {/* Stepper */}
        <div className="flex-1 order-2 lg:order-1 min-w-0">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1" data-testid="pipeline-stepper">
            {STAGES.map((stage, idx) => {
              const Icon = stage.icon;
              const isActive = !isRejected && idx === effectiveIdx;
              const isComplete = !isRejected && effectiveIdx > idx;
              const dotClass = isActive
                ? 'bg-tp-accent border-tp-accent text-white shadow-md'
                : isComplete
                  ? 'bg-tp-accent/20 border-tp-accent text-tp-accent'
                  : 'bg-white border-gray-300 text-gray-400';
              const connectorClass = idx < STAGES.length - 1
                ? (isComplete ? 'bg-tp-accent' : 'bg-gray-200')
                : 'hidden';

              return (
                <React.Fragment key={stage.id}>
                  <div className="flex flex-col items-center gap-1 shrink-0" data-testid={`stage-${stage.id}`}>
                    <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${dotClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className={`text-[10px] sm:text-xs text-center font-medium max-w-[70px] leading-tight ${isActive ? 'text-tp-accent font-semibold' : 'text-tp-text-light'}`}>
                      {stage.short}
                    </span>
                  </div>
                  {idx < STAGES.length - 1 && (
                    <div className={`h-0.5 flex-1 min-w-[16px] self-start mt-5 ${connectorClass}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Rejected banner (side-track) */}
          {isRejected && (
            <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="pipeline-rejected-banner">
              <XCircle className="h-4 w-4 shrink-0" />
              <span className="font-medium">Candidat rejeté</span>
              <span className="text-red-500">— changez l'état ci-contre pour le réintégrer au pipeline.</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default CandidatePipelineStatus;
